import { MAX_HOUSEHOLD_RECIPES } from './types.js';

export class PlatewellError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = new.target.name;
  }
}

export class NotFoundError extends PlatewellError {
  constructor(kind: string, id: string) {
    super(`${kind} ${id} not found`, 'not_found');
  }
}

/**
 * Thrown before any local mutation when the caller holds the viewer role. The Durable
 * Object rejects viewer writes too; this is the client-side half of that pair, so a
 * read-only user never even produces a local update that would later be discarded.
 */
export class ReadOnlyError extends PlatewellError {
  constructor(operation: string) {
    super(`Cannot ${operation}: you have read-only access to this household`, 'read_only');
  }
}

export class ValidationError extends PlatewellError {
  constructor(message: string) {
    super(message, 'validation');
  }
}

export class SystemCollectionError extends PlatewellError {
  constructor(operation: string) {
    super(`Cannot ${operation} a system collection`, 'system_collection');
  }
}

/**
 * Thrown before any local mutation that would take the household past its plan's recipe
 * limit. The Durable Object reports the same condition as a `quota_exceeded` sync error;
 * this is the client-side half of that pair, and the only half that works offline.
 */
export class QuotaExceededError extends PlatewellError {
  constructor(
    readonly limit: number,
    readonly count: number,
    message = `Your plan is limited to ${limit} recipes`,
    code = 'quota_exceeded',
  ) {
    super(message, code);
  }
}

/**
 * Thrown when a household holds `MAX_HOUSEHOLD_RECIPES`, on any plan.
 *
 * A subclass so every caller that stops at the plan cap (a share import keeping what
 * fitted, an import run halting) stops here too. Only the wording differs: subscribing
 * cannot lift this, so anything that offers the paywall must check for it first.
 */
export class RecipeCeilingError extends QuotaExceededError {
  constructor(count: number) {
    super(
      MAX_HOUSEHOLD_RECIPES,
      count,
      `A household can hold at most ${MAX_HOUSEHOLD_RECIPES.toLocaleString('en-US')} recipes`,
      'recipe_ceiling',
    );
  }
}

/** Thrown for every operation while a household's payment has failed. */
export class SubscriptionLapsedError extends PlatewellError {
  constructor() {
    super('This household\u2019s subscription payment did not go through', 'subscription_lapsed');
  }
}
